import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../AuthContext';
import { auth } from '../../firebase';
import { Mail, ArrowRight, RefreshCw, AlertCircle, Loader2 } from 'lucide-react';

interface InlineEmailVerificationProps {
    theme?: 'light' | 'dark';
    onVerified: () => void;
}

const InlineEmailVerification: React.FC<InlineEmailVerificationProps> = ({ theme = 'light', onVerified }) => {
    const { currentUser } = useAuth();
    const [resending, setResending] = useState(false);
    const [checking, setChecking] = useState(false);
    const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

    const isDark = theme === 'dark';

    // Auto-detect verification via polling
    const handleAutoVerified = useCallback(async () => {
        try {
            await auth.currentUser?.getIdToken(true);
            onVerified();
        } catch {
            // Fallback: still call onVerified even if token refresh fails
            onVerified();
        }
    }, [onVerified]);

    useEffect(() => {
        if (!currentUser) return;

        const interval = setInterval(async () => {
            try {
                await currentUser.reload();
                if (currentUser.emailVerified) {
                    clearInterval(interval);
                    handleAutoVerified();
                }
            } catch {
                // Silently ignore polling errors
            }
        }, 4000);

        return () => clearInterval(interval);
    }, [currentUser, handleAutoVerified]);

    const handleResend = async () => {
        if (!currentUser?.email) return;

        setResending(true);
        setMessage(null);

        try {
            const response = await fetch('/.netlify/functions/send-verification-email', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: currentUser.email }),
            });

            if (!response.ok) {
                throw new Error('Nepodařilo se poslat ověřovací e-mail');
            }

            setMessage({ text: 'Ověřovací e-mail byl úspěšně odeslán.', type: 'success' });
        } catch (err: any) {
            console.error('Error resending verification email:', err);
            setMessage({ text: 'Při odesílání e-mailu došlo k chybě. Zkuste to prosím později.', type: 'error' });
        } finally {
            setResending(false);
        }
    };

    const handleCheckVerification = async () => {
        if (!currentUser) return;
        setChecking(true);
        setMessage(null);

        try {
            // Force reload the user data from Firebase to get fresh emailVerified status
            await currentUser.reload();

            if (currentUser.emailVerified) {
                // Force token refresh so onAuthStateChanged fires with updated emailVerified
                await auth.currentUser?.getIdToken(true);
                onVerified();
            } else {
                setMessage({ text: 'E-mail zatím nebyl ověřen. Zkontrolujte svou schránku a klikněte na odkaz.', type: 'error' });
            }
        } catch (err) {
            console.error('Error checking verification:', err);
            setMessage({ text: 'Při kontrole ověření došlo k chybě.', type: 'error' });
        } finally {
            setChecking(false);
        }
    };

    return (
        <div className={`flex flex-col items-center justify-center space-y-6 w-full animate-in fade-in duration-300 ${isDark ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
            <div className={`p-4 rounded-full ${isDark ? 'bg-indigo-900/40' : 'bg-indigo-100 dark:bg-indigo-900/40'}`}>
                <Mail className={`w-10 h-10 ${isDark ? 'text-indigo-400' : 'text-indigo-600 dark:text-indigo-400'}`} />
            </div>

            <div className="text-center space-y-2">
                <h3 className="text-xl font-bold">
                    Ověřte svůj e-mail
                </h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500 dark:text-gray-400'}`}>
                    Odeslali jsme e-mail na <span className={`font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700 dark:text-gray-300'}`}>{currentUser?.email}</span>.
                    Klikněte na odkaz v e-mailu pro dokončení registrace.
                </p>
            </div>

            {message && (
                <div className={`w-full p-4 rounded-xl flex items-start text-sm ${message.type === 'success'
                    ? isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                    {message.type === 'error' && <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />}
                    <p>{message.text}</p>
                </div>
            )}

            <div className="w-full space-y-3">
                <button
                    onClick={handleCheckVerification}
                    disabled={checking}
                    className={`w-full flex justify-center items-center py-4 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white transition-all
            ${isDark
                            ? 'bg-[#1cb0f6] hover:bg-[#1cb0f6]/90 shadow-[0_4px_0_#1899d6] active:shadow-none active:translate-y-[4px] disabled:opacity-50'
                            : 'bg-brand-orange hover:bg-brand-orange/90 shadow-[0_4px_0_rgb(194,65,12)] active:shadow-none active:translate-y-[4px] disabled:opacity-50 dark:bg-brand-orange dark:hover:bg-brand-orange/90 dark:shadow-[0_4px_0_rgb(194,65,12)]'
                        }`}
                >
                    {checking ? (
                        <Loader2 className="animate-spin h-5 w-5" />
                    ) : (
                        <>
                            Už jsem e-mail ověřil
                            <ArrowRight className="ml-2 w-4 h-4" />
                        </>
                    )}
                </button>

                <button
                    onClick={handleResend}
                    disabled={resending || checking}
                    className={`w-full flex justify-center items-center py-4 px-4 rounded-xl text-sm font-bold transition-colors
            ${isDark
                            ? 'bg-[#1f2937] text-white hover:bg-gray-700 disabled:opacity-50'
                            : 'bg-white text-gray-700 border-2 border-gray-200 hover:bg-gray-50 disabled:opacity-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-700'
                        }`}
                >
                    {resending ? (
                        <>
                            <RefreshCw className="mr-2 w-4 h-4 animate-spin" />
                            Odesílání...
                        </>
                    ) : (
                        'Znovu odeslat e-mail'
                    )}
                </button>
            </div>
        </div>
    );
};

export default InlineEmailVerification;
